/** Shapes returned by `GET /api/generations/history` — the unified feed that
 *  every generator page renders. Items keep the field names of their own table
 *  and are told apart by `type`. */

export interface VideoHistoryItem {
	id: string;
	prompt: string;
	/** 上游模型标识，展示名走 getVideoModelLabel */
	model: string;
	resolution: string;
	duration: number;
	creditCost: number;
	status: string;
	videoUrl: string | null;
	/** 支持多图之前的旧列，等于 inputImageUrls[0]；老记录只有它 */
	inputImageUrl: string;
	inputImageUrls: string[];
	/** Clips submitted together share one id, so history can merge them */
	batchId: string | null;
	errorMessage: string | null;
	favorite: boolean;
	createdAt: string;
	/** 生成工具与输入范式。image-to-video 的行靠 `tool` 认出来，
	 *  展示模型名和回填表单都要读它，见 video-model-label.ts */
	metadata?: {
		tool?: string;
		mode?: string;
		modelId?: string;
		aspectRatio?: string;
		/** 提交时所在页面（2026-10-08 起，去掉语种前缀；/templates/* 合成 /templates），特效页认自己的片用 */
		page?: string;
		/** image-to-video reference 模式带的参考视频（2026-10-10 起） */
		referenceVideoUrls?: string[];
		referenceAudioUrls?: string[];
		width?: number;
		height?: number;
		fps?: number;
		duration?: number;
	} | null;
}

export type HistoryEntry =
	| { kind: "videos"; items: VideoHistoryItem[] }
	| { kind: "image"; item: { type: "image" } & ImageHistoryItem };

/** Collapses clips of the same submission into a single history entry. The feed
 *  is sorted by time and a batch is written in one go, so its rows always land
 *  next to each other. Rows without a batchId (anything generated before
 *  batches existed) each stay on their own. */
export function groupHistoryEntries(
	items: GenerationHistoryItem[],
): HistoryEntry[] {
	const entries: HistoryEntry[] = [];

	for (const item of items) {
		if (item.type !== "video") {
			entries.push({ kind: "image", item });
			continue;
		}

		const last = entries.at(-1);
		if (
			item.batchId &&
			last?.kind === "videos" &&
			last.items[0].batchId === item.batchId
		) {
			last.items.push(item);
		} else {
			entries.push({ kind: "videos", items: [item] });
		}
	}

	return entries;
}

export interface ImageHistoryItem {
	id: string;
	originalPrompt: string;
	model: string;
	status: string;
	errorMessage?: string | null;
	imageUrls: string[];
	inputImageUrls: string[];
	/** Indexes into imageUrls that the user starred — favoriting is per image */
	favoriteImageIndexes?: number[];
	createdAt: string;
	/** 生成参数原样存库，详情弹窗要读比例和清晰度档位 */
	metadata?: {
		/** 出图页面，10/23 起才有；作品回出生页按它认（image-home.ts） */
		page?: string;
		parameters?: {
			num_outputs?: number;
			aspect_ratio?: string;
			resolution?: string;
		};
		provider?: { parameters?: { resolution?: string } };
	} | null;
}

/** A reference image the user uploaded, with or without a generation after it.
 *  Only returned when the request asks for `includeUploads` — the generator
 *  sidebars show generations only. */
export interface UploadHistoryItem {
	id: string;
	imageUrl: string;
	favorite: boolean;
	createdAt: string;
}

export type GenerationHistoryItem =
	| ({ type: "image" } & ImageHistoryItem)
	| ({ type: "video" } & VideoHistoryItem);

export type AssetHistoryItem =
	| GenerationHistoryItem
	| ({ type: "upload" } & UploadHistoryItem);

/** My Assets tabs.
 *  - `image` is generated images only; uploaded reference images live under
 *    `all` (their tiles are labelled "Upload"), so the two never double up.
 *  - starred uploads also show up under `favorite`, alongside images and videos.
 *  - `music` (11/03) is its own list of song cards, not grid tiles, so songs
 *    only show up there and never under `all` / `favorite`. */
export const ASSET_FILTERS = [
	"all",
	"image",
	"video",
	"music",
	"favorite",
] as const;

export type AssetFilter = (typeof ASSET_FILTERS)[number];

/** Same vocabulary, minus Video — the picker only picks images. */
export const PICKER_ASSET_FILTERS = ["all", "image", "favorite"] as const;

/** Shared prefix. Invalidate with this to hit every history view at once. */
export const GENERATION_HISTORY_QUERY_KEY = ["generation-history-infinite"];

/** Page size of the image generator's history feed. */
export const IMAGE_HISTORY_PAGE_SIZE = 10;

/**
 * The image generator's exact key. Several call sites write to this cache with
 * `setQueryData`, which — unlike `invalidateQueries` — needs the full key, so
 * the page size has to be part of it: the video feed shares the prefix but
 * pages by 8, and a shared entry would make page 2 compute the wrong offset.
 */
export const IMAGE_HISTORY_QUERY_KEY = [
	...GENERATION_HISTORY_QUERY_KEY,
	IMAGE_HISTORY_PAGE_SIZE,
];

/** Tabs above a generator's history feed. Same words as the My Assets tabs
 *  minus Favorite — the feed is a timeline, not a library. */
export const HISTORY_FILTERS = ["all", "image", "video"] as const;

export type HistoryFilter = (typeof HISTORY_FILTERS)[number];

/** Cache key of one feed. `all` keeps the bare `[prefix, pageSize]` key so the
 *  call sites that write optimistic items into IMAGE_HISTORY_QUERY_KEY stay
 *  correct without having to know which tab is open. */
export function historyQueryKey(pageSize: number, filter: HistoryFilter) {
	return filter === "all"
		? [...GENERATION_HISTORY_QUERY_KEY, pageSize]
		: [...GENERATION_HISTORY_QUERY_KEY, pageSize, filter];
}

/** 成功的记录不出徽章，所以这里只有出问题和还没好这两种 */
export interface HistoryStatusLabels {
	generating: string;
	failed: string;
}

export const DEFAULT_HISTORY_STATUS_LABELS: HistoryStatusLabels = {
	generating: "Generating",
	failed: "Failed",
};

/** Short date format shared by every history card, e.g. "7-24 15:27" */
export function formatHistoryDate(dateString: string) {
	const date = new Date(dateString);
	return `${date.getMonth() + 1}-${date.getDate()} ${date.getHours()}:${date
		.getMinutes()
		.toString()
		.padStart(2, "0")}`;
}
